import React from 'react';
import { Card, Form, Badge, Row, Col } from 'react-bootstrap';
import { TemplateComposicao } from '../../../../model/banca/ComposicaoDisponibilidade';

interface TemplateSelectorDefinirProps {
    templates: TemplateComposicao[];
    selectedTemplateId: string | null;
    onTemplateSelect: (templateId: string) => void;
}

const TemplateSelectorDefinir: React.FC<TemplateSelectorDefinirProps> = ({
    templates,
    selectedTemplateId,
    onTemplateSelect
}) => {
    if (templates.length <= 1) {
        return null;
    }

    const formatarPapel = (papel: string): string => {
        const mapa: Record<string, string> = {
            'TITULAR': 'Titulares',
            'TECNICO': 'Técnicos', 
            'SECRETARIO': 'Secretários',
            'SUPLENTE': 'Suplentes'
        };
        return mapa[papel] || papel;
    };

    const formatarLimite = (minimo: number, maximo: number): string => {
        if (maximo >= 2147483647 || maximo > 100) return `Mín: ${minimo}`;
        return minimo === maximo ? `${maximo}` : `${minimo}-${maximo}`;
    };

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-primary text-white">
                <h6 className="mb-0"><i className="bi bi-list-check me-2"></i>Selecionar Template de Composição</h6>
            </Card.Header>
            <Card.Body>
                <p className="text-muted mb-3">Escolha o template que define a composição de membros para esta banca:</p>
                
                {templates.map(template => {
                    const titularPapel = template.papeis.find(p => p.papel === 'TITULAR');
                    const outrosPapeis = template.papeis.filter(p => p.papel !== 'TITULAR');
                    
                    return (
                        <Card 
                            key={template.id} 
                            className={`mb-3 cursor-pointer border-2 ${
                                selectedTemplateId === template.id ? 'border-primary bg-light' : 'border-light'
                            }`}
                            onClick={() => onTemplateSelect(template.id)}
                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                            <Card.Body>
                                <Form.Check
                                    type="radio"
                                    id={`template-${template.id}`}
                                    name="templateSelection"
                                    checked={selectedTemplateId === template.id}
                                    onChange={() => onTemplateSelect(template.id)}
                                    className="mb-3"
                                    label={
                                        <div className="w-100">
                                            <div className="fw-bold text-primary mb-1">{template.nome}</div>
                                            {template.descricao && (
                                                <div className="text-muted small mb-2">{template.descricao}</div>
                                            )}
                                        </div>
                                    }
                                />
                                
                                <Row className="g-2">
                                    {titularPapel && (
                                        <Col md={6}>
                                            <div className="border rounded p-2 bg-white">
                                                <div className="fw-semibold text-success mb-2">
                                                    <i className="bi bi-person-check me-1"></i>
                                                    {formatarPapel(titularPapel.papel)}
                                                    <Badge bg="success" className="ms-2">
                                                        {formatarLimite(titularPapel.minimo, titularPapel.maximo)}
                                                    </Badge>
                                                </div>
                                                {Object.keys(titularPapel.vinculosObrigatorios || {}).length > 0 && (
                                                    <div className="small">
                                                        <div className="text-muted mb-1">Vínculos obrigatórios:</div>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {Object.entries(titularPapel.vinculosObrigatorios).map(([vinculo, qtd]) => (
                                                                <Badge key={vinculo} bg="outline-success" text="success" className="border">
                                                                    {vinculo}: {qtd > 100 ? `Mín: ${qtd}` : qtd}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    )}
                                    
                                    {outrosPapeis.length > 0 && (
                                        <Col md={6}>
                                            <div className="border rounded p-2 bg-white">
                                                <div className="fw-semibold text-info mb-2">
                                                    <i className="bi bi-people me-1"></i>
                                                    Outros Membros
                                                </div>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {outrosPapeis.map(papel => (
                                                        <Badge key={papel.papel} bg="info" className="small">
                                                            {formatarPapel(papel.papel)}: {formatarLimite(papel.minimo, papel.maximo)}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    );
                })}
            </Card.Body>
        </Card>
    );
};

export default TemplateSelectorDefinir;
